import type { FunctionComponent, ReactNode } from 'react';
import { useEffect, useRef, useState } from 'react';

import { IHistoryGame } from '../../../api/fetchers/history/types';
import { Logger } from '../../../logger/Logger';
import { useOlderHistoryFetcher, useTodayHistoryFetcher } from '../../../store/apiHooks/useHistoryFetcher';
import { useGameStateSlice } from '../../../store/slices/game/selectors';
import { useHistorySlice } from '../../../store/slices/history/selectors';
import { Button, ButtonVariant } from '../../controls/button/Button';
import { IconButton, Icons } from '../../controls/iconButton/IconButton';
import { Loader } from '../../loader';
import { L12nText } from '../../text/L12nText';
import { HistoryMessage } from '../message';

import { HistoryGame } from './game';

import styles from './HistoryList.module.css';

export interface HistoryListProps {
  onSelect?: (id: IHistoryGame) => void;
}

export const HistoryListConnected: FunctionComponent<HistoryListProps> = ({ onSelect }: HistoryListProps) => {
  const history = useHistorySlice.getHistory();
  const gameId = useGameStateSlice.gameId();

  const [page, setPage] = useState<number>(-1);
  const [showScrollButton, setShowScrollButton] = useState<boolean>(false);
  const container = useRef<HTMLDivElement>(null);
  const throttleScroll = useRef<boolean>(false);

  const todayHistoryFetcher = useTodayHistoryFetcher();
  const olderHistoryFetcher = useOlderHistoryFetcher();

  useEffect(() => {
    if (!~page) {
      todayHistoryFetcher();
      setPage(history.page);
    } else if (page > history.page) {
      loadData(page);
    }
  }, [history.page, page]);

  useEffect(() => {
    const element = container.current?.parentElement;
    element?.addEventListener('scroll', handleScroll, { passive: true });
    return () => element?.removeEventListener('scroll', handleScroll);
  }, []);

  function loadData(page: number): void {
    void olderHistoryFetcher(page);
  }

  function scrollTop(): void {
    container.current?.parentElement?.scrollTo({
      top: 0,
      behavior: 'smooth',
    });
  }

  function handleScroll(): void {
    if (throttleScroll.current) return;

    throttleScroll.current = true;
    setTimeout(() => {
      setShowScrollButton(container.current?.parentElement ? container.current?.parentElement?.scrollTop > 0 : false);
      throttleScroll.current = false;
    }, 100);
  }

  return (
    <div data-testid="history-list" className={styles.HistoryList} ref={container}>
      {((): ReactNode => {
        if (history.loading && !history.ids.length) {
          return (
            <div className={styles.centered}>
              <Loader />
            </div>
          );
        } else if (history.error) {
          return (
            <div className={styles.centered}>
              <HistoryMessage title="ERROR_TITLE" message="ERROR_TEXT" />
              <div className={styles.loadMore}>
                <Button
                  tk="RELOAD"
                  variant={ButtonVariant.MAIN}
                  disabled={!history.loadMore}
                  loading={history.loading}
                  onClick={(): void => loadData(page)}
                />
              </div>
            </div>
          );
        } else {
          const showMessage = history.ids.length == 0;
          return (
            <div className={`${styles.content} ${showMessage ? styles.centered : ''}`}>
              {showMessage ? (
                <HistoryMessage title="NO_GAMES_TITLE" message="NO_GAMES_TEXT" />
              ) : (
                history.ids.map((id: string | number, index: number, array: (string | number)[]) => {
                  if (index === 0 && gameId === id) return;
                  return (
                    <HistoryGame
                      key={id}
                      game={history.entities[id] as IHistoryGame}
                      onSelect={onSelect}
                      showDate={index == 0 || history.entities[id]?.day !== history.entities[array[index - 1]]?.day}
                    />
                  );
                })
              )}
              <div className={styles.loadMore}>
                <Button
                  tk="LOAD_MORE"
                  variant={ButtonVariant.MAIN}
                  disabled={!history.loadMore}
                  loading={history.loading}
                  onClick={(): void => {
                    setPage((page: number) => page + 1);
                    Logger.logEvent('bet_history_load_more');
                  }}
                ></Button>
                {!history.loadMore && (
                  <div className={styles.noContent}>
                    <L12nText tk="NO_MORE_BETS" />
                  </div>
                )}
              </div>
              <div className={styles.scrollButton} hidden={!showScrollButton}>
                <IconButton icon={Icons.ChevronUp} ghost={false} large={true} onClick={scrollTop} />
              </div>
            </div>
          );
        }
      })()}
    </div>
  );
};
